import { useState } from 'react';

/**
 * One-line chip surfacing the user's linked Meta identity (name +
 * profile picture) just below the Meta accounts list on the Home page.
 *
 * Data source: `public_profile` scope. The OAuth flow fetches
 * `/me?fields=id,name` at link time; the id powers the data-deletion
 * cascade (separate concern, persisted as `meta_user_id`), the name is
 * persisted as `meta_user_name` (migration 013) and surfaces here so
 * the user can verify which Meta identity they linked as.
 *
 * Why this surface exists: Meta App Review expects every requested
 * scope to have an on-screen, user-visible use. Without this chip,
 * `public_profile` was effectively a server-only scope (the id never
 * left the database), which is the kind of thing reviewers reject as
 * "requested but not used". See
 * `docs/META_APP_REVIEW_SCREENCAST.md` Step 5 + the `public_profile`
 * justification block.
 *
 * Picture URL is composed server-side from `meta_user_id` via Meta's
 * public Graph picture endpoint (publicly readable with
 * `redirect=true` — no token needed). The browser's `<img>` follows
 * the 302 transparently. We never persist the CDN URL because Meta's
 * picture URLs aren't durable.
 *
 * Rendered once per identity, not per linked account: all rows in
 * `linkedMetaAccountDetails` share the same FB identity (one user can
 * link multiple ad accounts under the same Meta auth), so we pull the
 * first entry with a non-null name to drive the chip. The picture URL
 * is independent — present or null is fine; the initials fallback
 * covers the null case.
 *
 * Graceful degradation:
 *   - Pre-013 rows: `metaUserName` is null → no chip rendered. (Some
 *     legacy rows have `meta_user_id` populated but no name yet; the
 *     backfill script picks them up.)
 *   - Picture URL but no name: still no chip — the name is the
 *     primary affordance; the avatar alone is just decoration.
 *   - Picture load error: fall back to an initials avatar (component
 *     state). The chip stays visible; only the image swaps.
 */

interface MetaIdentityChipProps {
  details: Array<{
    metaUserName: string | null;
    metaUserPictureUrl: string | null;
  }>;
}

function getInitials(name: string): string {
  // Two-letter cap matches common avatar conventions: first + LAST
  // token initials. "Michelle de Vries" → "MV" (not "MD"); "Cher" →
  // "CH"; mixed-script names work as long as `\s+` splits them.
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function MetaIdentityChip({ details }: MetaIdentityChipProps) {
  // Truthy filter on the name field, expressed as a type predicate so
  // TypeScript narrows `identity.metaUserName` to `string` downstream
  // (avoids a `!` non-null assertion + a redundant runtime guard).
  // Skips entries where `metaUserName` is null, undefined, OR empty
  // string — the last one matters because earlier list entries with
  // `''` would otherwise short-circuit the search and hide the chip
  // even when a LATER entry has a real name (Copilot round 7).
  type IdentityDetail = MetaIdentityChipProps['details'][number];
  type IdentityWithName = IdentityDetail & { metaUserName: string };
  // `.trim().length > 0` rather than bare `.length > 0` — whitespace-
  // only names (`'   '`) would otherwise render a chip with a blank
  // gap after "Linked as". The backend already strips whitespace in
  // `_fetch_meta_user_identity`, but mirroring the rule client-side
  // hardens against legacy rows / direct DB writes that bypass the
  // OAuth path.
  const hasName = (d: IdentityDetail): d is IdentityWithName =>
    typeof d.metaUserName === 'string' && d.metaUserName.trim().length > 0;
  const identity = details.find(hasName);
  // Track the URL that failed to load (not a boolean) so a fresh URL
  // — e.g. after a re-link or after the backfill repopulates
  // `meta_user_id` — automatically re-enables the `<img>`. A sticky
  // boolean would lock the chip into the initials fallback for the
  // session even if a later render has a perfectly good URL, which
  // is exactly the Meta-CDN-transient-403 → re-link recovery path we
  // care about. No `useEffect` dance needed because the equality
  // check below is just a render-time comparison.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  // No entry passed the truthy-name filter above — render nothing.
  // Pre-013 / pre-backfill rows where every `metaUserName` is null
  // land here, as do empty `details` arrays.
  if (!identity) {
    return null;
  }

  const initials = getInitials(identity.metaUserName);
  const showImage =
    identity.metaUserPictureUrl != null &&
    identity.metaUserPictureUrl !== failedUrl;

  return (
    <div
      className="flex items-center gap-2 text-xs text-gray-500 mb-2 pl-7"
    >
      {/* No `role="status"` — the chip is static UI, not a live region.
          The visible "Linked as <Name>" span is self-describing to screen
          readers as plain text; an aria-label / status role here would
          trigger spurious announcements on every mount. */}
      {showImage ? (
        <img
          src={identity.metaUserPictureUrl!}
          alt=""
          aria-hidden="true"
          width={20}
          height={20}
          className="rounded-full border border-gray-200 bg-gray-100"
          onError={() => setFailedUrl(identity.metaUserPictureUrl)}
          // Don't leak the user's Meta picture URL to third-party
          // referrers when the browser navigates away mid-render.
          referrerPolicy="no-referrer"
        />
      ) : (
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-[10px] font-semibold text-gray-600"
        >
          {initials}
        </span>
      )}
      <span>
        Linked as <span className="text-gray-900">{identity.metaUserName}</span>
      </span>
    </div>
  );
}
